/**
 * Cobro de Pagos — migración web de ares4010 (escritorio).
 *
 * Busca un préstamo activo, carga su tabla de amortización, distribuye el
 * monto recibido entre las cuotas (de la más vieja a la más nueva: mora →
 * interés → capital) y registra el pago vía /api/guardardatospagos (misma
 * lógica transaccional que ya usaba el backend). Imprime el recibo al final.
 *
 * Incluye el descuento de mora (solo administradores) y el cobro de ventas
 * generales/GPS con saldo asociadas al préstamo — crédito o contado, igual
 * que el escritorio, que no filtra por tipofactura (botón "Ventas
 * pendientes" en la cabecera, visible siempre que hay un préstamo cargado;
 * abre el diálogo de facturas). Las ventas SIN préstamo asociado se cobran
 * en Registrar Pagos Ventas Generales (ares9650), no aquí — igual que el
 * escritorio, que filtra gps por noprest.
 */
import React, { useState, useMemo, useCallback, useEffect } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import InputAdornment from "@mui/material/InputAdornment";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Radio from "@mui/material/Radio";
import { DataGrid } from "@mui/x-data-grid";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import MoneyOffOutlinedIcon from "@mui/icons-material/MoneyOffOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import FolderSharedOutlinedIcon from "@mui/icons-material/FolderSharedOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import DirectionsCarOutlinedIcon from "@mui/icons-material/DirectionsCarOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import { useNavigate } from "react-router-dom";
import ReporteCrediticio from "modules/prestamos/reportecrediticio";
import CalculadoraPrestamos from "modules/prestamos/calculadoraprestamos";
import { imprimirTicketRecibo } from "modules/pagos/mantenimientopagos";
import { imprimirReciboPdf } from "lib/recibopdf";
import { imprimirReciboVentaGeneralPorNumero } from "modules/ventasgenerales/pagosventasgenerales";
import { usePortalQr, portalQrActual } from "lib/portalqr";
import { useGlobalMDAlert, useProductosMaterialContext } from "context";
import { tienePermisosModulo } from "lib/seguridad/permisosPorModulo";
import { FP, PageHeader, StatCard, fpInputSx, fpDataGridSx } from "components/financepro";
import { useApi } from "lib/api";
import { estadoPlanCompletivo } from "lib/completivoplan";
import { PlanCompletivoResumen } from "components/completivos/PlanCompletivo";
import tiposDocumentosJson from "utils/tiposDocumentos.json";

const TIPO_DOCUMENTO_MOTOR_PAGOS_PRESTAMOS = "Pagos Prestamos";
const DOCUMENTO_DIARIO_PAGO_PRESTAMO = "PAGO PRESTAMO";

const moduloContabilidadInstalado = (modulos) => {
  if (modulos == null || modulos === false || modulos === "false") return false;
  const partes = Array.isArray(modulos) ? modulos : String(modulos).split(/[,;]/);
  return partes.some((m) => String(m || "").trim().toLowerCase() === "contabilidad");
};

const enviarMotorContableActivo = (valor) => String(valor || "").trim().toUpperCase() === "S";

const debeEnviarAlMotor = (modulos, enviarMotorContable) =>
  moduloContabilidadInstalado(modulos) && enviarMotorContableActivo(enviarMotorContable);

const bloqueMotorDeTipo = (tipooperacion) => {
  const clave = String(tipooperacion || "").toLowerCase().replace(/\s/g, "");
  if (!clave) return null;
  return tiposDocumentosJson.find((e) => e?.[clave])?.[clave] || null;
};

const origenFormaPagoReglas = (tipooperacion) => {
  const bloque = bloqueMotorDeTipo(tipooperacion);
  if (!bloque) return [];
  const raw = bloque.origenformapago ?? bloque.origenformadepago ?? [];
  return (Array.isArray(raw) ? raw : [raw]).map((x) => String(x || "").trim()).filter(Boolean);
};

const tipoTieneOrigenFormaPago = (tipooperacion) => origenFormaPagoReglas(tipooperacion).length > 0;

const payloadFormaPagoSiAplica = (tipoDocumento, documentoFormaPago) => {
  const tipo = String(tipoDocumento || "").trim();
  const documento = String(documentoFormaPago || "").trim();
  if (!tipo || !tipoTieneOrigenFormaPago(tipo) || !documento) return {};
  return { forma_pago: documento };
};

const codigoUsaOrigenFormaPago = (tipooperacion, codigoRegla) => {
  const codigo = String(codigoRegla || "").trim().toLowerCase();
  if (!codigo) return false;
  return origenFormaPagoReglas(tipooperacion).some((c) => c.toLowerCase() === codigo);
};

const documentoMotorDefinido = (registros, tipoDocumento) => {
  const tipo = String(tipoDocumento || "").trim();
  if (!tipo) return false;
  return (registros || []).some((r) => {
    if (String(r?.tipo_documento || "").trim() !== tipo) return false;
    if (codigoUsaOrigenFormaPago(tipo, r?.codigo_regla)) return true;
    const aplica = String(r?.aplica_a || "cuenta").trim().toLowerCase();
    if (aplica === "json") return true;
    return Boolean(String(r?.cuentacontable || "").trim());
  });
};

const reglasMotorDeDocumento = (registros, tipoDocumento) => {
  const tipo = String(tipoDocumento || "").trim();
  return (registros || []).filter((r) => String(r?.tipo_documento || "").trim() === tipo);
};

const CLAVES_META_MOTOR = new Set([
  "tipodocumento",
  "nfactura",
  "documento",
  "numero",
  "suplidor",
  "fecha",
  "tasa",
  "usuario",
  "concepto",
  "valor",
  "valor_total",
  "lineas",
  "cuentas",
]);

const redondearDinero2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const construirDocumentoMotorPagosPrestamos = (reglas, ctx) => {
  const tipoDoc = TIPO_DOCUMENTO_MOTOR_PAGOS_PRESTAMOS;
  const lineasFp = Array.isArray(ctx.lineas)
    ? ctx.lineas.filter((ln) => ln?.cuenta && (ln.monto || 0) > 0)
    : [];
  const valor = redondearDinero2(ctx.valorTotal);
  const montos = ctx.montos || {};
  const doc = {
    tipodocumento: tipoDoc,
    nfactura: ctx.nfactura,
    documento: ctx.documento || DOCUMENTO_DIARIO_PAGO_PRESTAMO,
    numero: ctx.numero,
    fecha: ctx.fecha,
    tasa: ctx.tasa,
    usuario: ctx.usuario,
    concepto: ctx.concepto,
    valor,
    lineas: lineasFp,
    permitir_descuadre: true,
  };
  (reglas || []).forEach((regla) => {
    const codigo = String(regla?.codigo_regla || "").trim();
    if (!codigo || CLAVES_META_MOTOR.has(codigo.toLowerCase())) return;
    const usaFp =
      codigoUsaOrigenFormaPago(tipoDoc, codigo) ||
      String(regla?.aplica_a || "").trim().toLowerCase() === "json";
    if (usaFp) {
      doc[codigo] = lineasFp;
      return;
    }
    const extra = montos[codigo] ?? montos[codigo.toLowerCase()];
    doc[codigo] = redondearDinero2(extra != null ? extra : valor);
  });
  return doc;
};

const ALIAS_TIPOPAGO_FORMA = {
  Contado: ["Contado", "Efectivo"],
  Transferencias: ["Transferencias", "Transferencia"],
};

const resolverFilaFormaPago = (pago, formasPagoDb) => {
  const candidatos = [];
  const doc = String(pago?.documento || pago?.metodo || "").trim();
  if (doc) candidatos.push(doc);
  const aliases = ALIAS_TIPOPAGO_FORMA[doc] || [];
  aliases.forEach((a) => {
    if (!candidatos.some((c) => c.toLowerCase() === a.toLowerCase())) candidatos.push(a);
  });
  for (const cand of candidatos) {
    const norm = cand.toLowerCase();
    const fila =
      (formasPagoDb || []).find((r) => String(r.documento || "").trim().toLowerCase() === norm) ||
      (formasPagoDb || []).find((r) => String(r.documento || "").trim().toLowerCase().includes(norm)) ||
      (formasPagoDb || []).find((r) => norm.includes(String(r.documento || "").trim().toLowerCase()));
    if (fila) return fila;
  }
  return null;
};

const documentoFormaPagoDesdeTipopago = (tipopago, formasPagoDb) => {
  const fila = resolverFilaFormaPago({ documento: tipopago, metodo: tipopago }, formasPagoDb);
  return String(fila?.documento || tipopago || "").trim();
};

const lineasMotorDesdeTipopago = (tipopago, monto, formasPagoDb) => {
  const fila = resolverFilaFormaPago({ documento: tipopago, metodo: tipopago }, formasPagoDb);
  const cuenta = String(fila?.cuenta_contable || "").trim();
  const valor = redondearDinero2(monto);
  return cuenta && valor > 0 ? [{ cuenta, monto: valor }] : [];
};

// Filtro local del autocomplete: matchea por número de préstamo, nombre o
// cédula, y limita los resultados renderizados para que sea fluido con miles.
const filtrarPrestamos = createFilterOptions({
  stringify: (o) => `${o.noprest} ${o.cliente || ""} ${o.cedula || ""}`,
  limit: 80,
});

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Parsea montos que llegan como "6,784.83" (MySQL format) o números.
const toNum = (v) => {
  const n = parseFloat(String(v ?? "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Busque un préstamo para ver sus cuotas
      </Typography>
    </Stack>
  );
}

// Distribuye `total` entre las cuotas (orden ascendente): por cada cuota paga
// primero la mora, luego interés+capital, hasta agotar el monto. Replica
// ares4010.asignarpagos, ajustado al contrato de /api/guardardatospagos.
// Campo "Descuento de mora" oculto para todos los roles por decisión del
// negocio (2026-10-16), también para los admin. La lógica queda intacta: sin el
// campo el descuento vale 0 y el cobro se comporta como si no existiera.
const MOSTRAR_DESCUENTO_MORA = false;

// `descuentoMora` (solo admin) rebaja la mora exigible antes de cobrar.
// `completivo` (saldo del inicial pactado) se descuenta primero, como los
// cargos fijos de incauto/llamada.
function distribuir(cuotas, total, incauto, llamada, descuentoMora = 0, completivo = 0) {
  let restante = total - incauto - llamada - completivo;
  let descRestante = descuentoMora;
  let totalMora = 0;
  let totalCapInt = 0;
  let totalMoraDesc = 0;
  let totalDescInt = 0;
  const detalle = cuotas.map((c) => {
    const moraOriginal = toNum(c.Mora);
    // Descuento de mora aplicado de la cuota más vieja a la más nueva.
    const moraDesc = Math.min(descRestante, moraOriginal);
    descRestante -= moraDesc;
    const mora = moraOriginal - moraDesc; // mora que realmente se cobra
    const valor = toNum(c.Valor); // interés+capital pendiente NETO (ya restado el descinte)
    let moraPagada = 0;
    let pagado = 0;
    if (restante > 0.001) {
      moraPagada = Math.min(restante, mora);
      restante -= moraPagada;
    }
    if (restante > 0.001) {
      pagado = Math.min(restante, valor);
      restante -= pagado;
    }
    // Descuento de interés (ares3030): el descinte configurado completa el
    // saldo de la cuota, así que solo se materializa cuando el pago la salda.
    const cierra = pagado >= valor - 0.01;
    const descInt = cierra ? toNum(c.descinte) : 0;
    // El descuento de mora solo vale en las cuotas que el pago alcanza: en
    // las demás la mora se recalcula completa en el próximo cobro, así que
    // ni se muestra, ni se imprime, ni se graba (pagos.descmora).
    const tocada = moraPagada > 0 || pagado > 0 || descInt > 0;
    const moraDescEf = tocada ? moraDesc : 0;
    totalMora += moraPagada;
    totalCapInt += pagado;
    totalDescInt += descInt;
    totalMoraDesc += moraDescEf;
    return {
      ...c,
      _moraPagada: moraPagada,
      _moraDesc: moraDescEf,
      _pagado: pagado,
      _descinte: descInt,
    };
  });
  return {
    detalle,
    totalMora,
    totalCapInt,
    totalMoraDesc,
    totalDescInt,
    totalRecibo: totalMora + totalCapInt, // porción del préstamo (sin incauto/llamada)
    excedente: Math.max(0, restante),
  };
}

// Fecha local de hoy en formato YYYY-MM-DD (para el input type="date").
const hoyISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
};

export default function CobroPagos({ embedded = false, onExit, onSaved, noprestInicial } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  usePortalQr();
  const { controller, selectedCompany, showSuccess, showWarning } = useProductosMaterialContext();
  const { roleDetalles, usuarioLog, claveLog, modulos, enviarMotorContable } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || "";
  const [formasPagoDb, setFormasPagoDb] = useState([]);

  // ---- Fecha del pago (antes pantalla aparte "Cobro de Pagos Especial") ----
  // Con ares4015 el operador puede editar la fecha: la mora y la distribución
  // se calculan a esa fecha y el backend re-valida el permiso cuando llega
  // fechapago distinta de hoy.
  const puedeFecha = tienePermisosModulo(roleDetalles, "ares4015");

  // Reimprimir un recibo tiene permiso propio (el backend re-valida). El botón
  // no estaba protegido: se mostraba a todo el mundo y el servidor devolvía 403.
  const puedeReimprimir = tienePermisosModulo(roleDetalles, "reimprimirpagos");
  const [fecha, setFecha] = useState(hoyISO());

  // ---- Perfil (rol admin para el descuento de mora; datos de la compañía) ----
  const [esadmin, setEsadmin] = useState(false);
  const [compania, setCompania] = useState(null);
  // varinicial.colocarmoraenrecibo = 'S': el recibo detalla la mora pendiente
  // (misma bandera que usa el escritorio en imprimirrecibopagoprestamo).
  const [moraEnRecibo, setMoraEnRecibo] = useState(false);
  useEffect(() => {
    let activo = true;
    post("/api/cobropago_perfil", {}).then(({ ok, data }) => {
      if (!activo || !ok) return;
      setEsadmin(!!data.esadmin);
      setCompania(data.compania || null);
      setMoraEnRecibo(String(data.colocarmoraenrecibo || "N").toUpperCase() === "S");
    });
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Búsqueda (todos los préstamos activos, filtrado local) ----
  const [opciones, setOpciones] = useState([]);
  const [buscando, setBuscando] = useState(false);

  const cargarPrestamos = useCallback(async () => {
    setBuscando(true);
    try {
      const { ok, data } = await post("/api/cobropago_prestamos", {});
      if (ok && Array.isArray(data.prestamos)) setOpciones(data.prestamos);
    } finally {
      setBuscando(false);
    }
  }, [post]);

  useEffect(() => {
    cargarPrestamos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Préstamo cargado ----
  const [cargando, setCargando] = useState(false);
  const [noprest, setNoprest] = useState(null);
  const [prestamo, setPrestamo] = useState(null); // dataprestamo[0]
  const [cuotas, setCuotas] = useState([]); // ordenadas ascendente por nocuota
  const [nombreCliente, setNombreCliente] = useState("");
  const [comentario, setComentario] = useState("");
  const [cuentasActivas, setCuentasActivas] = useState(0);

  const cargar = useCallback(
    async (np) => {
      if (!np) return;
      setCargando(true);
      try {
        // Con ares4015 la amortización (mora incluida) se calcula a la fecha del pago.
        const { ok, data } = await post(
          "/api/recuperartablaamortizacion",
          puedeFecha ? { noprest: np, fechapago: fecha } : { noprest: np }
        );
        if (!ok) {
          showAlert({ message: data.Error || "No se pudo cargar el préstamo", color: "error" });
          return;
        }
        const filas = (data.data || []).slice().sort((a, b) => toNum(a.nocuota) - toNum(b.nocuota));
        if (!filas.length) {
          showAlert({ message: "El préstamo no tiene cuotas pendientes", color: "warning" });
        }
        const dp = (data.dataprestamo || [])[0] || null;
        setNoprest(np);
        setCuotas(filas);
        setPrestamo(dp);
        setNombreCliente(filas[0]?.Nombres || dp?.nombres || "");
        setComentario(data.comentario || "");
        setCuentasActivas(toNum((data.datacuentaactiva || [])[0]?.cedula));
        // Cargos fijos pendientes del préstamo (se cobran primero).
        // Se suman todas las filas abiertas, no solo la primera: el backend
        // cierra las que devuelve este mismo select (cancelacionchasis crea un
        // increg por chasis, asi que puede haber varias).
        setIncauto((data.increg || []).reduce((a, r) => a + toNum(r.valor), 0));
        setLlamada(toNum(dp?.llamada));
        // Completivo del inicial pendiente. Con plan de fechas se propone
        // cobrar solo lo vencido a la fecha del pago (el tope sigue siendo el
        // total, para adelantar partes); sin plan, el total como siempre.
        const plan = Array.isArray(data.completivoplan) ? data.completivoplan : [];
        const pend = toNum(data.completivototal ?? data.completivo);
        const est = plan.length ? estadoPlanCompletivo(plan, pend, fecha || hoyISO()) : null;
        const propuesto = est ? est.vencido : pend;
        setCompletivoPend(pend);
        setCompletivoPlan(plan);
        setCompletivo(propuesto > 0 ? String(propuesto) : "");
        setValor("");
        setDescuentoMora("");
        // Ventas generales con saldo pendiente (gps; crédito o contado
        // asociado a préstamo — el escritorio no filtra por tipofactura).
        post("/api/cobropago_ventascredito", { noprest: np }).then(({ ok: ok2, data: d2 }) => {
          setVentasCredito(ok2 && Array.isArray(d2.ventas) ? d2.ventas : []);
        });
      } finally {
        setCargando(false);
      }
    },
    [post, showAlert, puedeFecha, fecha]
  );

  // Si cambia la fecha con un préstamo cargado, recalcula la amortización a
  // la nueva fecha (la mora depende de la fecha del pago).
  useEffect(() => {
    if (puedeFecha && noprest && fecha) cargar(noprest);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fecha]);

  // Preselección: al abrir desde el expediente ya viene el préstamo elegido.
  useEffect(() => {
    if (noprestInicial) cargar(String(noprestInicial));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Panel de pago ----
  const [valor, setValor] = useState("");
  // Cargos fijos del préstamo (no los teclea el usuario): incauto viene de
  // increg.valor y llamada de prestamo.llamada. Se cobran antes que las cuotas.
  const [incauto, setIncauto] = useState(0);
  const [llamada, setLlamada] = useState(0);
  // Completivo del inicial: saldo del inicial pactado en la venta (factmot)
  // menos lo abonado en la tabla inicial. No es editable: al cargar muestra
  // lo vencido y al digitar el valor toma de él hasta el pendiente total.
  const [completivoPend, setCompletivoPend] = useState(0);
  const [completivo, setCompletivo] = useState("");
  // Plan de fechas del completivo ([{ fecha, monto }]); vacío = todo exigible.
  const [completivoPlan, setCompletivoPlan] = useState([]);
  const [tipopago, setTipopago] = useState("Contado");
  const [descripcionpago, setDescripcionpago] = useState("");
  const [descuentoMora, setDescuentoMora] = useState("");
  const [guardando, setGuardando] = useState(false);

  const cargarFormasPago = useCallback(async () => {
    if (!usuarioLog || !selectedCompany?.db_empresa || !moduloContabilidadInstalado(modulos)) {
      setFormasPagoDb([]);
      return;
    }
    try {
      const response = await fetch(`${servidor}/configuracionbackend_listarformasdepago`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || "",
          password: claveLog || "",
        }),
      });
      const data = await response.json().catch(() => ({}));
      setFormasPagoDb(response.ok ? data.formas_pago || [] : []);
    } catch {
      setFormasPagoDb([]);
    }
  }, [selectedCompany, usuarioLog, claveLog, servidor, modulos]);

  useEffect(() => {
    cargarFormasPago();
  }, [cargarFormasPago]);

  const enviarAlMotorContablePagosPrestamos = useCallback(
    async (ctxDocumento) => {
      if (!debeEnviarAlMotor(modulos, enviarMotorContable)) return;
      if (!selectedCompany?.db_empresa) return;
      try {
        const tipoDoc = TIPO_DOCUMENTO_MOTOR_PAGOS_PRESTAMOS;
        const reglasRes = await fetch(`${servidor}/contabilidadbackend_listarmotorcontable`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id_empresa: selectedCompany,
            usuario: usuarioLog || "",
            password: claveLog || "",
            tipodocumento: tipoDoc,
            ...payloadFormaPagoSiAplica(tipoDoc, ctxDocumento.formaPago),
          }),
        });
        const reglasData = await reglasRes.json().catch(() => ({}));
        if (!reglasRes.ok) {
          showWarning(reglasData.Error || "No se pudieron consultar las reglas del motor contable");
          return;
        }
        const reglasDoc = reglasMotorDeDocumento(reglasData.registros, tipoDoc);
        if (!documentoMotorDefinido(reglasDoc, tipoDoc)) {
          showWarning(
            "No hay reglas del motor para Pagos Prestamos; no se registró en diario/histdia"
          );
          return;
        }
        const payloadDocumento = construirDocumentoMotorPagosPrestamos(reglasDoc, ctxDocumento);
        const motorRes = await fetch(`${servidor}/contabilidadbackend_enviarmotorcontable`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id_empresa: selectedCompany,
            usuario: usuarioLog || "",
            password: claveLog || "",
            modulos,
            documento: payloadDocumento,
          }),
        });
        const motorData = await motorRes.json().catch(() => ({}));
        if (!motorRes.ok) {
          showWarning(motorData.Error || "No se pudo enviar el pago al motor contable");
          return;
        }
        if (motorData.omitido) return;
        if (motorData.ok) {
          if (motorData.descuadrado) {
            showWarning(motorData.mensaje || "Motor contable: asiento guardado descuadrado");
          } else {
            showSuccess(motorData.mensaje || "Motor contable: asiento generado");
          }
        } else if (motorData.mensaje) {
          showWarning(motorData.mensaje);
        }
      } catch {
        showWarning("Error de conexión al enviar el pago al motor contable");
      }
    },
    [
      modulos,
      enviarMotorContable,
      servidor,
      selectedCompany,
      usuarioLog,
      claveLog,
      showWarning,
      showSuccess,
    ]
  );

  // ---- Ventas a crédito (gps) ----
  const [ventasCredito, setVentasCredito] = useState([]);
  const [ventaOpen, setVentaOpen] = useState(false);
  const [ventaSel, setVentaSel] = useState(null);
  const [ventaMonto, setVentaMonto] = useState("");
  const [ventaTipo, setVentaTipo] = useState("Contado");
  const [ventaRef, setVentaRef] = useState("");
  const [pagandoVenta, setPagandoVenta] = useState(false);

  const abrirVentas = () => {
    setVentaSel(null);
    setVentaMonto("");
    setVentaTipo("Contado");
    setVentaRef("");
    setVentaOpen(true);
  };

  // ---- Barra de opciones del préstamo (toolbar de ares4010) ----
  // 'crediticio' | 'nota' | 'matricula' | 'garantes' | 'evaluacion'
  const navigate = useNavigate();
  const [accion, setAccion] = useState(null);
  const [notaEdit, setNotaEdit] = useState("");
  const [guardandoNota, setGuardandoNota] = useState(false);
  const [matricula, setMatricula] = useState(null); // { chasis, pagos } | null mientras carga
  const [cedulaGar, setCedulaGar] = useState("");
  const [garante, setGarante] = useState(null);
  const [consultandoGar, setConsultandoGar] = useState(false);

  const abrirNota = () => {
    setNotaEdit(prestamo?.nota || "");
    setAccion("nota");
  };
  const guardarNota = async () => {
    setGuardandoNota(true);
    try {
      const { ok, data } = await post("/api/cobropago_guardarnota", {
        noprest,
        nota: notaEdit.trim(),
      });
      if (ok) {
        showAlert({ message: data.ok || "Nota guardada", color: "success" });
        setPrestamo((prev) => (prev ? { ...prev, nota: data.nota ?? notaEdit.trim() } : prev));
        setAccion(null);
      } else {
        showAlert({ message: data.Error || "No se pudo guardar la nota", color: "error" });
      }
    } finally {
      setGuardandoNota(false);
    }
  };

  const abrirMatricula = async () => {
    setMatricula(null);
    setAccion("matricula");
    const { ok, data } = await post("/api/cobropago_matricula", { noprest });
    if (ok) setMatricula({ chasis: data.chasis || "", pagos: data.pagos || [] });
    else {
      showAlert({ message: data.Error || "No se pudo consultar la matrícula", color: "error" });
      setAccion(null);
    }
  };

  // ---- Reimprimir recibos (reimprimirpagos): lista los recibos del
  // préstamo y reutiliza el ticket/endpoint de Mantenimiento Pagos ----
  const [recibos, setRecibos] = useState(null); // null = cargando
  const [reimprimiendo, setReimprimiendo] = useState(null); // norecibo en curso

  const abrirRecibos = async () => {
    setRecibos(null);
    setAccion("recibos");
    const { ok, data } = await post("/api/ares4030_recibos", { noprest: String(noprest) });
    if (ok) setRecibos(data.recibos || []);
    else {
      showAlert({ message: data.Error || "No se pudieron recuperar los recibos", color: "error" });
      setAccion(null);
    }
  };

  const reimprimirRecibo = async (norecibo) => {
    setReimprimiendo(norecibo);
    try {
      const { ok, data } = await post("/api/ares4030_reimprimirrecibo", { norecibo });
      // Multi-empresa: el backend devuelve el membrete de la empresa del pago.
      if (ok && data.recibo) imprimirTicketRecibo(data.recibo, data.compania || compania);
      else showAlert({ message: data.Error || "No se pudo reimprimir el recibo", color: "error" });
    } finally {
      setReimprimiendo(null);
    }
  };

  // ---- Modificar foto (ares4010.modificarfoto): expedienteunico.foto ----
  const [foto, setFoto] = useState(null); // b64 actual ("" = sin foto, null = cargando)
  const [fotoNueva, setFotoNueva] = useState(""); // b64 de la imagen seleccionada
  const [guardandoFoto, setGuardandoFoto] = useState(false);

  const abrirFoto = async () => {
    setFoto(null);
    setFotoNueva("");
    setAccion("foto");
    const { ok, data } = await post("/api/cobropago_foto", { cedula: prestamo?.cedula });
    if (ok) setFoto(data.foto_b64 || "");
    else {
      showAlert({ message: data.Error || "No se pudo recuperar la foto", color: "error" });
      setAccion(null);
    }
  };

  const seleccionarFoto = (e) => {
    const archivo = e.target.files?.[0];
    e.target.value = ""; // permite volver a elegir el mismo archivo
    if (!archivo) return;
    if (!archivo.type.startsWith("image/")) {
      showAlert({ message: "El archivo debe ser una imagen", color: "warning" });
      return;
    }
    // El servidor la reduce a 1600 px antes de guardarla (app/imagenes.py).
    if (archivo.size > 12 * 1024 * 1024) {
      showAlert({ message: "La imagen no puede exceder 12MB", color: "warning" });
      return;
    }
    const lector = new FileReader();
    lector.onload = () => {
      const b64 = String(lector.result || "").split(",")[1] || "";
      if (b64) setFotoNueva(b64);
    };
    lector.readAsDataURL(archivo);
  };

  const guardarFoto = async () => {
    if (!fotoNueva) {
      showAlert({ message: "Seleccione una imagen primero", color: "warning" });
      return;
    }
    setGuardandoFoto(true);
    try {
      const { ok, data } = await post("/api/cobropago_guardarfoto", {
        cedula: prestamo?.cedula,
        foto_b64: fotoNueva,
      });
      if (ok) {
        showAlert({ message: data.ok || "Foto actualizada", color: "success" });
        setAccion(null);
      } else {
        showAlert({ message: data.Error || "No se pudo guardar la foto", color: "error" });
      }
    } finally {
      setGuardandoFoto(false);
    }
  };

  const abrirGarantes = () => {
    setCedulaGar(prestamo?.cedula || "");
    setGarante(null);
    setAccion("garantes");
  };
  const consultarGarante = async () => {
    if (!cedulaGar.trim()) {
      showAlert({ message: "Indique la cédula", color: "warning" });
      return;
    }
    setConsultandoGar(true);
    setGarante(null);
    try {
      const { ok, data } = await post("/api/ares2030_garante", { cedula: cedulaGar.trim() });
      if (ok) setGarante(data);
      else showAlert({ message: data.Error || "No hay datos para esta cédula", color: "error" });
    } finally {
      setConsultandoGar(false);
    }
  };
  const pagarVenta = async () => {
    if (!ventaSel) {
      showAlert({ message: "Seleccione una factura", color: "warning" });
      return;
    }
    const monto = toNum(ventaMonto);
    if (monto <= 0) {
      showAlert({ message: "Indique el valor a pagar", color: "warning" });
      return;
    }
    if (monto > toNum(ventaSel.pendiente) + 0.001) {
      showAlert({ message: "El valor excede la deuda pendiente", color: "warning" });
      return;
    }
    if (ventaTipo !== "Contado" && !ventaRef.trim()) {
      showAlert({ message: "Indique la referencia del pago", color: "warning" });
      return;
    }
    setPagandoVenta(true);
    try {
      const { ok, data } = await post("/api/cobropago_pagarventacredito", {
        noprest,
        nfactura: ventaSel.id,
        cedula: prestamo?.cedula,
        valor: monto,
        tipopago: ventaTipo,
        referencia: ventaRef.trim(),
      });
      if (ok) {
        showAlert({ message: data.mensajeok || "Pago registrado", color: "success" });
        // Recibo del cobro: el pago ya quedó registrado, así que un fallo al
        // imprimir solo avisa (se reimprime desde Mantenimiento de pagos).
        if (data.norecibo) {
          const res = await imprimirReciboVentaGeneralPorNumero(post, data.norecibo);
          if (!res.ok) {
            showAlert({
              message: "Pago registrado, pero no se pudo imprimir el recibo #" + data.norecibo,
              color: "warning",
            });
          }
        }
        // Refresca la lista de ventas pendientes.
        const { ok: ok2, data: d2 } = await post("/api/cobropago_ventascredito", { noprest });
        const lista = ok2 && Array.isArray(d2.ventas) ? d2.ventas : [];
        setVentasCredito(lista);
        if (!lista.length) setVentaOpen(false);
        else {
          setVentaSel(null);
          setVentaMonto("");
          setVentaRef("");
        }
      } else {
        showAlert({ message: data.Error || "Error al registrar el pago", color: "error" });
      }
    } finally {
      setPagandoVenta(false);
    }
  };

  const moraDisponible = useMemo(() => cuotas.reduce((s, c) => s + toNum(c.Mora), 0), [cuotas]);

  // Distribución reactiva del monto entre las cuotas.
  const dist = useMemo(
    () =>
      distribuir(
        cuotas,
        toNum(valor),
        toNum(incauto),
        toNum(llamada),
        toNum(descuentoMora),
        toNum(completivo)
      ),
    [cuotas, valor, incauto, llamada, descuentoMora, completivo]
  );

  const pendientePrestamo = prestamo ? toNum(prestamo.pendiente) : 0;
  const totalRecibido = dist.totalCapInt; // capital+interés (reduce el balance)
  const totalCobrado = dist.totalRecibo + toNum(incauto) + toNum(llamada) + toNum(completivo);

  // Datos generales derivados de las cuotas pendientes.
  const capitalPendiente = useMemo(
    () => cuotas.reduce((s, c) => s + toNum(c.scapital), 0),
    [cuotas]
  );
  const interesPendiente = useMemo(
    () => cuotas.reduce((s, c) => s + toNum(c.sinteres), 0),
    [cuotas]
  );
  // Atraso (ares4010 "Atraso+Mora"): pendiente neto + mora de las cuotas ya
  // vencidas a la fecha del pago. c.Fecha llega como dd/mm/yyyy.
  // La comparación es estricta: la cuota que vence el mismo día del corte
  // todavía no está atrasada (ares4010 usa diasdeatraso.days > 0, y el
  // expediente fecha < CURDATE()); con <= se contaba un día de más.
  const atrasoPendiente = useMemo(() => {
    const corte = fecha || hoyISO();
    return cuotas.reduce((s, c) => {
      const [d, m, y] = String(c.Fecha || "").split("/");
      const vencida = y && `${y}-${m}-${d}` < corte;
      return vencida ? s + toNum(c.Valor) + toNum(c.Mora) : s;
    }, 0);
  }, [cuotas, fecha]);
  const ventasCreditoTotal = useMemo(
    () => ventasCredito.reduce((s, v) => s + toNum(v.pendiente), 0),
    [ventasCredito]
  );
  // Card "Atraso + Mora": incluye los cargos fijos que se cobran junto con el
  // recibo (incauto, llamadas y completivo del inicial), igual que el monto
  // en atraso del expediente.
  // Del completivo con plan solo cuenta lo vencido a la fecha del pago.
  const estadoCompletivo = useMemo(
    () =>
      completivoPlan.length
        ? estadoPlanCompletivo(completivoPlan, completivoPend, fecha || hoyISO())
        : null,
    [completivoPlan, completivoPend, fecha]
  );
  const completivoExigible = estadoCompletivo ? estadoCompletivo.vencido : completivoPend;
  const atrasoTotal = atrasoPendiente + toNum(incauto) + toNum(llamada) + completivoExigible;
  const saldoTotal = pendientePrestamo + moraDisponible; // saldo incluyendo mora

  const limpiar = () => {
    setNoprest(null);
    setPrestamo(null);
    setCuotas([]);
    setNombreCliente("");
    setComentario("");
    setCuentasActivas(0);
    setValor("");
    setIncauto(0);
    setLlamada(0);
    setCompletivoPend(0);
    setCompletivo("");
    setCompletivoPlan([]);
    setDescripcionpago("");
    setTipopago("Contado");
    setDescuentoMora("");
    setVentasCredito([]);
    setVentaOpen(false);
  };

  // Recibo del cobro (mismo contenido que ares4010.imprimirrecibopagoprestamo),
  // en PDF con el formato de bankivo (lib/recibopdf).
  const imprimirRecibo = (norecibo, datos) => {
    const filasCuotas = datos.detalle
      .filter((c) => c._pagado > 0 || c._moraPagada > 0)
      .map((c) => {
        const pend = Math.max(
          0,
          toNum(c.Valor) - c._pagado + (toNum(c.Mora) - c._moraDesc - c._moraPagada)
        );
        return [datos.noprest, c.nocuota, fmtMoney(c._pagado), fmtMoney(c._moraPagada), fmtMoney(pend)];
      });
    // Pie del escritorio (bloque final de imprimirrecibopagoprestamo): atraso
    // post-pago de las cuotas vencidas, mora pendiente y saldo total.
    // Mismo criterio estricto que el card de arriba: la cuota que vence el día
    // del corte aún no cuenta como atraso.
    const corte = datos.fechaCorte || hoyISO();
    const esVencida = (c) => {
      const [dd, mm, yy] = String(c.Fecha || "").split("/");
      return Boolean(yy) && `${yy}-${mm}-${dd}` < corte;
    };
    let montoAtraso = 0;
    let moraPendiente = 0;
    datos.detalle.forEach((c) => {
      if (esVencida(c)) montoAtraso += Math.max(0, toNum(c.Valor) - c._pagado);
      moraPendiente += Math.max(0, toNum(c.Mora) - c._moraDesc - c._moraPagada);
    });
    // Balance tras el pago sumando el pendiente por cuota: c.Valor ya viene
    // neto del descinte, así el descuento que salda una cuota sí rebaja el
    // balance. (El "pendiente" del préstamo que trae el backend ya es neto de
    // descuentos y abonos, pero no del descinte aún no materializado de las
    // cuotas por saldar; la suma por cuota es la exacta.)
    const balancePrestamo = datos.detalle.reduce(
      (s, c) => s + Math.max(0, toNum(c.Valor) - c._pagado),
      0
    );
    const totalAtraso = montoAtraso + (datos.moraEnRecibo ? moraPendiente : 0);
    // "Balance Pend." es capital+interés sin mora: la misma cifra que imprime
    // la reimpresión (pagosres.balanceanterior). Un pago que se va completo a
    // mora no lo baja, y eso es correcto. El total CON mora solo se imprime
    // cuando el tenant pide la mora en el recibo (colocarmoraenrecibo); si
    // no, la nota del pie ya avisa que los balances no la incluyen y sumarla
    // hacía parecer que el pago no se había restado.
    const totalPendiente = balancePrestamo + moraPendiente;
    const now = new Date();
    imprimirReciboPdf({
      titulo: "Recibo de Pago",
      compania: datos.compania,
      campos: [
        ["Recibo #", String(norecibo || "").padStart(15, "0")],
        ["Prestamo", datos.noprest],
        ["Cliente", datos.nombreCliente],
        ["Fecha", `${now.toLocaleDateString("es-DO")} ${now.toLocaleTimeString("es-DO")}`],
        datos.fechapago ? ["Fecha pago", datos.fechapago] : null,
      ],
      tabla: { head: ["Prest.", "Cuota", "Valor", "Mora", "Pend."], body: filasCuotas },
      totales: [
        { etiqueta: "Pagado", valor: datos.totalCapInt },
        datos.totalMora > 0 ? { etiqueta: "Mora", valor: datos.totalMora } : null,
        datos.totalMoraDesc > 0 ? { etiqueta: "Mora descontada", valor: datos.totalMoraDesc } : null,
        toNum(datos.incauto) ? { etiqueta: "Incauto/Visitas", valor: datos.incauto } : null,
        toNum(datos.llamada) ? { etiqueta: "Cargos", valor: datos.llamada } : null,
        toNum(datos.completivo) ? { etiqueta: "Completivo Inicial", valor: datos.completivo } : null,
        datos.totalDescInt > 0 ? { etiqueta: "Desc.", valor: datos.totalDescInt } : null,
        { etiqueta: "Totales", valor: datos.totalCobrado, destacado: true },
        { etiqueta: "Monto Atraso", valor: montoAtraso },
        datos.moraEnRecibo ? { etiqueta: "Mora Pend.", valor: moraPendiente } : null,
        { etiqueta: "Total Atraso", valor: totalAtraso },
        { etiqueta: "Balance Pend.", valor: balancePrestamo },
        datos.moraEnRecibo ? { etiqueta: "Total Pend.", valor: totalPendiente } : null,
      ],
      notas: [
        datos.moraEnRecibo
          ? "*Mora calculada solo al dia de hoy"
          : "****Los balances no tienen la mora incluida",
        ...(datos.totalDescInt > 0 ? ["**Los descuentos son valores informativos"] : []),
      ],
      firma: "Preparado Por",
      qr: portalQrActual(),
    });
  };

  const guardar = async () => {
    if (!noprest || !prestamo) {
      showAlert({ message: "Cargue un préstamo primero", color: "warning" });
      return;
    }
    if (puedeFecha && !fecha) {
      showAlert({ message: "Indique la fecha del pago", color: "warning" });
      return;
    }
    if (toNum(valor) <= 0) {
      showAlert({ message: "Indique el valor a pagar", color: "warning" });
      return;
    }
    if (toNum(completivo) < 0 || toNum(completivo) > completivoPend + 0.001) {
      showAlert({
        message: `El completivo no puede exceder el pendiente de RD$ ${fmtMoney(completivoPend)}`,
        color: "warning",
      });
      return;
    }
    if (toNum(incauto) + toNum(llamada) + toNum(completivo) > toNum(valor)) {
      showAlert({
        message: "El valor es insuficiente frente a los cargos de incauto/llamada/completivo",
        color: "warning",
      });
      return;
    }
    // Permite un cobro solo de cargos (incauto/llamada/completivo) aunque no cubra cuota.
    if (dist.totalRecibo <= 0 && toNum(incauto) + toNum(llamada) + toNum(completivo) <= 0) {
      showAlert({ message: "El monto no cubre ninguna cuota ni cargo", color: "warning" });
      return;
    }
    if (tipopago !== "Contado" && !descripcionpago.trim()) {
      showAlert({ message: "Indique la referencia del pago", color: "warning" });
      return;
    }
    const nosolic = cuotas[0]?.id;
    const datospago = dist.detalle
      .filter((c) => c._pagado > 0 || c._moraPagada > 0 || c._descinte > 0)
      .map((c) => ({
        Snoprest: c.Snoprest,
        nocuota: c.nocuota,
        Pagado: c._pagado.toFixed(2),
        Sinteres: toNum(c.sinteres),
        Mora: c._moraPagada.toFixed(2),
        PagMora: toNum(c.PagMora),
        Descinte: Number(c._descinte.toFixed(2)),
        // Descuento de mora de la cuota (pagos.descmora). Solo viaja en las
        // cuotas que este recibo toca: en las demás la mora se recalcula en
        // el próximo cobro y el descuento no llegó a aplicarse.
        Descmora: Number(c._moraDesc.toFixed(2)),
        Balance: c.Balance,
      }));

    // Atraso tras el pago (mismo cálculo del pie del recibo): se persiste en
    // pagosres para que la reimpresión pueda mostrar Monto/Total Atraso.
    const corteAtraso = fecha || hoyISO();
    let montoAtraso = 0;
    let moraAtraso = 0;
    dist.detalle.forEach((c) => {
      const [dd, mm, yy] = String(c.Fecha || "").split("/");
      if (yy && `${yy}-${mm}-${dd}` < corteAtraso)
        montoAtraso += Math.max(0, toNum(c.Valor) - c._pagado);
      moraAtraso += Math.max(0, toNum(c.Mora) - c._moraDesc - c._moraPagada);
    });

    setGuardando(true);
    try {
      const { ok, data } = await post("/api/guardardatospagos", {
        noprest,
        nosolic,
        cedula: prestamo.cedula,
        // Sucursal del usuario logueado (no la del prestamo). Se envia solo el
        // codigo numerico ("1-CENTRAL" -> "1") para mantener el mismo formato
        // con que se guardaba historicamente en pagos/pagosres.
        sucursal: String(localStorage.getItem("sucursal") ?? "").trim().split("-")[0],
        incauto: toNum(incauto),
        llamada: toNum(llamada),
        completivo: toNum(completivo),
        // pagosres.balanceanterior = balanceanterior - totalrecibido es el
        // balance que imprime la reimpresión: el descuento de interés que
        // este pago materializa también rebaja la deuda, así que va incluido.
        balanceanterior: pendientePrestamo,
        totalrecibido: totalRecibido + dist.totalDescInt,
        montoatraso: Number(montoAtraso.toFixed(2)),
        moraatraso: moraEnRecibo ? Number(moraAtraso.toFixed(2)) : 0,
        tipopago,
        descripcionpago: descripcionpago.trim(),
        datospago,
        ...(puedeFecha ? { fechapago: fecha } : {}),
      });
      if (ok) {
        showAlert({ message: "Pago registrado correctamente", color: "success" });
        const enviarMotor = debeEnviarAlMotor(modulos, enviarMotorContable);
        if (enviarMotor) {
          const totalMotor = redondearDinero2(totalCobrado);
          const documentoFormaPago = documentoFormaPagoDesdeTipopago(tipopago, formasPagoDb);
          const lineasMotor = lineasMotorDesdeTipopago(tipopago, totalMotor, formasPagoDb);
          await enviarAlMotorContablePagosPrestamos({
            valorTotal: totalMotor,
            formaPago: documentoFormaPago,
            lineas: lineasMotor,
            nfactura: String(data?.norecibo || ""),
            documento: DOCUMENTO_DIARIO_PAGO_PRESTAMO,
            numero: data?.norecibo,
            fecha: fecha || hoyISO(),
            tasa: 1,
            usuario: usuarioLog || "",
            concepto: `Pago préstamo ${noprest} recibo ${data?.norecibo || ""}`,
            montos: {
              cajageneral: totalMotor,
              ingresos: totalMotor,
            },
          });
        }
        imprimirRecibo(data?.norecibo, {
          detalle: dist.detalle,
          fechapago: puedeFecha && fecha !== hoyISO() ? fecha : "",
          // Multi-empresa: membrete de la empresa del préstamo (del backend).
          compania: data?.compania || compania,
          noprest,
          nombreCliente,
          totalCapInt: dist.totalCapInt,
          totalMora: dist.totalMora,
          totalMoraDesc: dist.totalMoraDesc,
          totalDescInt: dist.totalDescInt,
          incauto: toNum(incauto),
          llamada: toNum(llamada),
          completivo: toNum(completivo),
          totalCobrado,
          fechaCorte: fecha,
          moraEnRecibo,
        });
        limpiar();
        if (onSaved) onSaved();
      } else {
        showAlert({ message: data.Error || "Error al registrar el pago", color: "error" });
      }
    } catch {
      showAlert({ message: "Error de red o servidor", color: "error" });
    } finally {
      setGuardando(false);
    }
  };

  const columns = useMemo(
    () => [
      { field: "nocuota", headerName: "Cuota", width: 80, align: "center", headerAlign: "center" },
      { field: "Fecha", headerName: "Fecha", width: 120 },
      {
        field: "Valor",
        headerName: "Cuota (Cap+Int)",
        width: 150,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => "RD$ " + fmtMoney(value),
      },
      {
        field: "Mora",
        headerName: "Mora",
        width: 120,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => "RD$ " + fmtMoney(value),
      },
      {
        field: "_pagado",
        headerName: "Pagado",
        width: 130,
        align: "right",
        headerAlign: "right",
        renderCell: (p) => {
          const total = toNum(p.row._pagado) + toNum(p.row._moraPagada);
          return (
            <Typography
              sx={{
                marginTop: "14px",
                fontWeight: total > 0 ? 700 : 400,
                color: total > 0 ? FP.color.success : FP.color.textMuted,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              RD$ {fmtMoney(total)}
            </Typography>
          );
        },
      },
    ],
    []
  );

  // OJO: los datos de cuota traen su propio `id` (= solicit.id, igual para todas
  // las cuotas del préstamo). Va después del spread para no colisionar en el grid.
  const rows = dist.detalle.map((c, i) => ({ ...c, id: i }));

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Cobro de Pagos"
        subtitle="Registra el pago de cuotas de un préstamo con distribución de mora, interés y capital"
        cta={
          <Stack direction="row" spacing={1.2}>
            {embedded && (
              <Button startIcon={<ArrowBackIcon />} onClick={onExit} sx={btnOutline(FP.color.navy)}>
                Volver
              </Button>
            )}
            {!!noprest && (
              <Button
                startIcon={<StorefrontOutlinedIcon />}
                onClick={abrirVentas}
                sx={
                  ventasCredito.length > 0
                    ? btnSolid(FP.color.accentBlue, FP.color.navy)
                    : btnOutline(FP.color.textSecondary)
                }
              >
                Ventas pendientes ({ventasCredito.length})
              </Button>
            )}
            <Button startIcon={<RestartAltIcon />} onClick={limpiar} sx={btnOutline(FP.color.navy)}>
              Nuevo
            </Button>
          </Stack>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Búsqueda */}
        <Box sx={sectionBox}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={puedeFecha ? 5 : 7}>
              <Autocomplete
                options={opciones}
                loading={buscando}
                filterOptions={filtrarPrestamos}
                getOptionLabel={(o) => (o ? `#${o.noprest} — ${o.cliente || ""}` : "")}
                isOptionEqualToValue={(o, v) => o.noprest === v.noprest}
                renderOption={(props, o) => (
                  <li {...props} key={o.noprest}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                        #{o.noprest} — {o.cliente || ""}
                      </Typography>
                      {o.cedula ? (
                        <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                          {o.cedula}
                        </Typography>
                      ) : null}
                    </Box>
                  </li>
                )}
                onChange={(_, opt) => opt && cargar(opt.noprest)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Buscar préstamo (número, nombre o cédula)"
                    sx={fpInputSx}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ fontSize: 18, color: FP.color.textMuted }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            {puedeFecha && (
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="date"
                  label="Fecha del pago"
                  value={fecha}
                  onChange={(e) => setFecha(e.target.value)}
                  sx={fpInputSx}
                  InputLabelProps={{ shrink: true }}
                  helperText="El pago se registra a esta fecha"
                />
              </Grid>
            )}
            <Grid item xs={12} md={puedeFecha ? 4 : 5}>
              {cargando ? (
                <Stack
                  direction="row"
                  spacing={1.2}
                  alignItems="center"
                  sx={{ color: FP.color.textSecondary }}
                >
                  <CircularProgress size={18} />
                  <Typography>Cargando amortización…</Typography>
                </Stack>
              ) : noprest ? (
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Chip
                    icon={<PersonOutlineIcon />}
                    label={nombreCliente || `Préstamo #${noprest}`}
                    sx={{
                      bgcolor: FP.color.surfaceAlt,
                      color: FP.color.textSecondary,
                      fontWeight: 700,
                    }}
                  />
                  <Chip
                    label={`#${noprest}`}
                    sx={{
                      bgcolor: FP.color.accentBlueSoft,
                      color: FP.color.accentBlue,
                      fontWeight: 700,
                    }}
                  />
                  {prestamo?.periodos ? (
                    <Chip label={prestamo.periodos} variant="outlined" />
                  ) : null}
                </Stack>
              ) : null}
            </Grid>
          </Grid>
        </Box>

        {/* Opciones del préstamo (toolbar de ares4010: Expediente, Crediticio,
            Nota, Matrícula, Garantes, Evaluación) */}
        {noprest && prestamo && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2.5 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<FolderSharedOutlinedIcon />}
              sx={btnOutline(FP.color.navy)}
              onClick={() => navigate(`/prestamos/expediente/${noprest}`)}
            >
              Expediente
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AssessmentOutlinedIcon />}
              sx={btnOutline(FP.color.navy)}
              onClick={() => setAccion("crediticio")}
            >
              Crediticio
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<NotesOutlinedIcon />}
              sx={btnOutline(FP.color.navy)}
              onClick={abrirNota}
            >
              Nota préstamo
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<DirectionsCarOutlinedIcon />}
              sx={btnOutline(FP.color.navy)}
              onClick={abrirMatricula}
            >
              Matrícula
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<GroupOutlinedIcon />}
              sx={btnOutline(FP.color.navy)}
              onClick={abrirGarantes}
            >
              Garantes
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<CalculateOutlinedIcon />}
              sx={btnOutline(FP.color.navy)}
              onClick={() => setAccion("evaluacion")}
            >
              Evaluación
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<PhotoCameraOutlinedIcon />}
              sx={btnOutline(FP.color.navy)}
              onClick={abrirFoto}
            >
              Modificar foto
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<PrintOutlinedIcon />}
              sx={btnOutline(FP.color.navy)}
              onClick={abrirRecibos}
            >
              Reimprimir recibo
            </Button>
          </Stack>
        )}

        {noprest && (
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid item xs={6} md={6}>
              <StatCard
                label="Balance pendiente"
                value={`RD$ ${fmtMoney(pendientePrestamo)}`}
                icon={ReceiptLongOutlinedIcon}
                accent={FP.color.navy}
              />
            </Grid>
            <Grid item xs={6} md={6}>
              <StatCard
                label="Atraso + Mora + Incauto + Cargo"
                value={`RD$ ${fmtMoney(atrasoTotal)}`}
                icon={PaymentsOutlinedIcon}
                accent={FP.color.danger}
              />
            </Grid>
          </Grid>
        )}

        {/* Datos generales del préstamo (como ares4010) */}
        {noprest && prestamo && (
          <Box sx={sectionBox}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <ReceiptLongOutlinedIcon sx={{ fontSize: 18, color: FP.color.textSecondary }} />
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: "0.74rem",
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: FP.color.textSecondary,
                }}
              >
                Datos generales del préstamo
              </Typography>
            </Stack>
            <Grid container spacing={2}>
              <Dato label="Cédula" value={prestamo.cedula} />
              <Dato label="Tipo de pago" value={prestamo.periodos} />
              <Dato label="Cuentas activas" value={cuentasActivas} />
              <Dato label="Últ. fecha pago" value={prestamo.fultpago || "—"} />
              <Dato label="Valor últ. pago" money value={prestamo.montoult} />
              <Dato label="Recibido" money value={prestamo.recibido} />
              <Dato label="Capital pagado" money value={prestamo.vpagcap} />
              <Dato label="Mora pagada" money value={prestamo.vpagmora} />
              <Dato label="Descuento de interés" money value={prestamo.descuento} />
              <Dato label="Pendiente préstamo" money value={pendientePrestamo} />
              <Dato label="Capital pendiente" money value={capitalPendiente} />
              <Dato label="Interés pendiente" money value={interesPendiente} />
              <Dato label="Mora pendiente" money value={moraDisponible} />
              <Dato label="Saldo total (incl. mora)" money value={saldoTotal} strong />
              <Dato label="Ventas pendientes" money value={ventasCreditoTotal} />
              <Dato label="Incauto / Visita" money value={incauto} />
              <Dato label="Llamadas / Otros" money value={llamada} />
              {completivoPend > 0 ? (
                <Dato label="Completivo inicial pendiente" money value={completivoPend} strong />
              ) : null}
              {estadoCompletivo ? (
                <Dato label="Completivo vencido" money value={estadoCompletivo.vencido} />
              ) : null}
              {comentario ? <Dato label="Comentario" value={comentario} full /> : null}
              {prestamo.nota ? <Dato label="Notas" value={prestamo.nota} full /> : null}
            </Grid>
          </Box>
        )}

        <Grid container spacing={2.5}>
          {/* Tabla de cuotas */}
          <Grid item xs={12} md={8}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 2.5,
                border: `1px solid ${FP.color.border}`,
                boxShadow: FP.shadow.soft,
                overflow: "hidden",
              }}
            >
              <Box sx={{ px: 2.5, py: 1.8, borderBottom: `1px solid ${FP.color.border}` }}>
                <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary }}>
                  Cuotas pendientes
                </Typography>
              </Box>
              <Box sx={{ bgcolor: "#fff" }}>
                <DataGrid
                  autoHeight
                  rows={rows}
                  columns={columns}
                  loading={cargando}
                  rowHeight={48}
                  pageSizeOptions={[10, 25, 50, 100]}
                  initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                  slots={{ noRowsOverlay: NoRowsOverlay }}
                  sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "220px" }}
                  disableRowSelectionOnClick
                  getRowClassName={(p) =>
                    toNum(p.row._pagado) + toNum(p.row._moraPagada) > 0 ? "fila-pagada" : ""
                  }
                />
              </Box>
            </Card>
          </Grid>

          {/* Panel de pago */}
          <Grid item xs={12} md={4}>
            <Box sx={{ ...sectionBox, mb: 0 }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: "0.74rem",
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: FP.color.textSecondary,
                  mb: 2,
                }}
              >
                Pago
              </Typography>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  type="number"
                  label="Valor a pagar"
                  value={valor}
                  onChange={(e) => {
                    const v = e.target.value;
                    setValor(v);
                    // El completivo no se edita: toma el valor (después de
                    // incauto/llamada) hasta su pendiente total; lo que sobre
                    // va a las cuotas. Antes no bajaba de lo vencido y un pago
                    // menor obligaba a rebajarlo a mano (y se ponía en 0).
                    if (completivoPend > 0) {
                      const disponible = toNum(v) - toNum(incauto) - toNum(llamada);
                      const auto = Math.min(
                        completivoPend,
                        Math.max(0, Math.round(disponible * 100) / 100)
                      );
                      setCompletivo(auto > 0 ? String(auto) : "");
                    }
                  }}
                  disabled={!noprest}
                  sx={fpInputSx}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
                  }}
                />
                <TextField
                  select
                  fullWidth
                  label="Tipo de pago"
                  value={tipopago}
                  onChange={(e) => setTipopago(e.target.value)}
                  disabled={!noprest}
                  sx={fpInputSx}
                >
                  <MenuItem value="Contado">Contado</MenuItem>
                  <MenuItem value="Transferencias">Transferencias</MenuItem>
                </TextField>
                <TextField
                  fullWidth
                  label="Descripción / Referencia"
                  value={descripcionpago}
                  onChange={(e) => setDescripcionpago(e.target.value)}
                  disabled={!noprest}
                  sx={fpInputSx}
                />

                {MOSTRAR_DESCUENTO_MORA && esadmin && moraDisponible > 0 && (
                  <TextField
                    fullWidth
                    type="number"
                    label="Descuento de mora (admin)"
                    value={descuentoMora}
                    onChange={(e) => {
                      const v = Math.max(0, Math.min(toNum(e.target.value), moraDisponible));
                      setDescuentoMora(e.target.value === "" ? "" : String(v));
                    }}
                    helperText={`Mora disponible: RD$ ${fmtMoney(moraDisponible)}`}
                    sx={fpInputSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MoneyOffOutlinedIcon sx={{ fontSize: 18, color: FP.color.info }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}

                {(toNum(incauto) > 0 || toNum(llamada) > 0 || completivoPend > 0) && (
                  <Box
                    sx={{
                      border: `1px solid ${FP.color.warning}44`,
                      bgcolor: `${FP.color.warning}0c`,
                      borderRadius: 2,
                      p: 1.6,
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <TuneOutlinedIcon sx={{ fontSize: 17, color: FP.color.warning }} />
                      <Typography
                        sx={{
                          fontWeight: 800,
                          fontSize: "0.72rem",
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                          color: FP.color.warning,
                        }}
                      >
                        Cargos a cobrar primero
                      </Typography>
                    </Stack>
                    <Stack spacing={0.4}>
                      {toNum(incauto) > 0 ? <Row label="Incauto" value={toNum(incauto)} /> : null}
                      {toNum(llamada) > 0 ? (
                        <Row label="Cargo / Llamada" value={toNum(llamada)} />
                      ) : null}
                    </Stack>
                    {completivoPend > 0 && (
                      <TextField
                        fullWidth
                        type="number"
                        label="Completivo inicial"
                        value={completivo}
                        helperText={
                          estadoCompletivo
                            ? `Vencido: RD$ ${fmtMoney(
                                estadoCompletivo.vencido
                              )}. El valor a pagar se aplica primero al completivo (hasta RD$ ${fmtMoney(
                                completivoPend
                              )}) y el resto a las cuotas.`
                            : `Saldo del inicial pactado: RD$ ${fmtMoney(
                                completivoPend
                              )}. El valor a pagar se aplica primero al completivo y el resto a las cuotas.`
                        }
                        sx={{ ...fpInputSx, mt: 1.2 }}
                        InputProps={{
                          readOnly: true,
                          startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
                        }}
                      />
                    )}
                    {completivoPend > 0 && <PlanCompletivoResumen estado={estadoCompletivo} />}
                  </Box>
                )}

                <Divider />
                <Stack spacing={0.6}>
                  <Row label="Capital + Interés" value={dist.totalCapInt} />
                  <Row label="Mora" value={dist.totalMora} />
                  {dist.totalMoraDesc > 0 ? (
                    <Row label="Mora descontada" value={dist.totalMoraDesc} info />
                  ) : null}
                  {dist.totalDescInt > 0 ? (
                    <Row label="Descuento de interés" value={dist.totalDescInt} info />
                  ) : null}
                  {toNum(incauto) > 0 ? <Row label="Incauto" value={toNum(incauto)} /> : null}
                  {toNum(llamada) > 0 ? <Row label="Cargo" value={toNum(llamada)} /> : null}
                  {toNum(completivo) > 0 ? (
                    <Row label="Completivo inicial" value={toNum(completivo)} />
                  ) : null}
                  {dist.excedente > 0 ? (
                    <Row label="Excedente (no aplicado)" value={dist.excedente} warn />
                  ) : null}
                  <Divider sx={{ my: 0.6 }} />
                  <Row label="Total a cobrar" value={totalCobrado} bold />
                </Stack>

                <Button
                  fullWidth
                  size="large"
                  startIcon={
                    guardando ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />
                  }
                  onClick={guardar}
                  disabled={guardando || !noprest}
                  sx={{ ...btnSolid(FP.color.navy, FP.color.navySoft), mt: 1, py: 1.2 }}
                >
                  Cobrar
                </Button>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Diálogo: ventas a crédito (gps) */}
      <Dialog
        open={ventaOpen}
        onClose={() => setVentaOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1.2 }}>
          <StorefrontOutlinedIcon sx={{ color: FP.color.accentBlue }} />
          Ventas generales pendientes — Préstamo #{noprest}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.86rem", color: FP.color.textSecondary, mb: 2 }}>
            Selecciona la factura a cobrar e indica el monto (no puede exceder lo pendiente).
          </Typography>
          <Stack spacing={1} sx={{ mb: 2 }}>
            {ventasCredito.map((v) => {
              const sel = ventaSel && ventaSel.id === v.id;
              return (
                <Box
                  key={v.id}
                  onClick={() => {
                    setVentaSel(v);
                    setVentaMonto(String(v.pendiente));
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    p: 1.2,
                    borderRadius: 2,
                    cursor: "pointer",
                    border: `1px solid ${sel ? FP.color.accentBlue : FP.color.border}`,
                    bgcolor: sel ? `${FP.color.accentBlue}0c` : "#fff",
                  }}
                >
                  <Radio checked={!!sel} size="small" />
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.88rem" }}>
                      Fact #{v.id} — {v.tipogasto || "—"}
                      {v.tipofactura ? ` · ${v.tipofactura}` : ""}
                    </Typography>
                    <Typography sx={{ fontSize: "0.74rem", color: FP.color.textMuted }}>
                      {v.fecha}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary }}>
                    RD$ {fmtMoney(v.pendiente)}
                  </Typography>
                </Box>
              );
            })}
            {!ventasCredito.length && (
              <Typography
                sx={{ fontSize: "0.86rem", color: FP.color.textMuted, textAlign: "center", py: 2 }}
              >
                No hay ventas generales pendientes.
              </Typography>
            )}
          </Stack>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Valor a pagar"
                value={ventaMonto}
                onChange={(e) => setVentaMonto(e.target.value)}
                disabled={!ventaSel}
                sx={fpInputSx}
                InputProps={{
                  startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                fullWidth
                label="Tipo de pago"
                value={ventaTipo}
                onChange={(e) => setVentaTipo(e.target.value)}
                disabled={!ventaSel}
                sx={fpInputSx}
              >
                <MenuItem value="Contado">Contado</MenuItem>
                <MenuItem value="Transferencia">Transferencia</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Referencia"
                value={ventaRef}
                onChange={(e) => setVentaRef(e.target.value)}
                disabled={!ventaSel}
                sx={fpInputSx}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setVentaOpen(false)} sx={btnOutline(FP.color.textSecondary)}>
            Cerrar
          </Button>
          <Button
            disableElevation
            startIcon={
              pagandoVenta ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <PaymentsOutlinedIcon />
              )
            }
            onClick={pagarVenta}
            disabled={pagandoVenta || !ventaSel}
            sx={btnSolid(FP.color.accentBlue, FP.color.navy)}
          >
            Pagar factura
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Diálogos de la barra de opciones (toolbar ares4010) ---- */}

      {/* Crediticio: reporte del préstamo cargado (reutiliza la pantalla ares7040) */}
      <Dialog
        open={accion === "crediticio"}
        onClose={() => setAccion(null)}
        fullWidth
        maxWidth="lg"
        PaperProps={{ sx: { borderRadius: 2.5 } }}
      >
        <DialogContent sx={{ p: 0 }}>
          {accion === "crediticio" && <ReporteCrediticio noprestInicial={noprest} />}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAccion(null)} sx={btnOutline(FP.color.textSecondary)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Nota préstamo (ponernotaaprestamos): edita prestamo.notaaprestamo */}
      <Dialog
        open={accion === "nota"}
        onClose={() => setAccion(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 2.5 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Nota del préstamo #{noprest}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Nota"
            value={notaEdit}
            onChange={(e) => setNotaEdit(e.target.value)}
            inputProps={{ maxLength: 1000 }}
            sx={{ ...fpInputSx, mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setAccion(null)} sx={btnOutline(FP.color.textSecondary)}>
            Cancelar
          </Button>
          <Button
            disableElevation
            startIcon={
              guardandoNota ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />
            }
            onClick={guardarNota}
            disabled={guardandoNota}
            sx={btnSolid(FP.color.accentBlue, FP.color.navy)}
          >
            Guardar nota
          </Button>
        </DialogActions>
      </Dialog>

      {/* Matrícula: pagos de matrícula del chasis (informacionmatricula) */}
      <Dialog
        open={accion === "matricula"}
        onClose={() => setAccion(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 2.5 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Detalle matrícula</DialogTitle>
        <DialogContent>
          {matricula === null ? (
            <Stack alignItems="center" sx={{ py: 3 }}>
              <CircularProgress size={26} />
            </Stack>
          ) : matricula.pagos.length ? (
            <Stack spacing={1}>
              {matricula.chasis ? (
                <Typography sx={{ fontSize: "0.85rem", color: FP.color.textSecondary }}>
                  Chasis: {matricula.chasis}
                </Typography>
              ) : null}
              {matricula.pagos.map((p) => (
                <Stack
                  key={p.id}
                  spacing={0.4}
                  sx={{
                    p: 1.2,
                    borderRadius: 1.5,
                    border: `1px solid ${FP.color.border}`,
                    bgcolor: FP.color.surfaceAlt,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography sx={{ fontSize: "0.9rem", fontWeight: 700 }}>
                      {p.fecha} — RD$ {fmtMoney(p.valor)}
                    </Typography>
                    <Chip
                      size="small"
                      label={p.financiado ? "Financiada en cuotas" : "Pagada"}
                      sx={{
                        height: 20,
                        fontSize: "0.68rem",
                        fontWeight: 800,
                        color: "#fff",
                        bgcolor: p.financiado ? FP.color.warning : FP.color.success,
                      }}
                    />
                  </Stack>
                  {p.financiado && (
                    <Typography sx={{ fontSize: "0.82rem", color: FP.color.textSecondary }}>
                      Cobrado RD$ {fmtMoney(p.cobrado)} · Pendiente RD$ {fmtMoney(p.pendiente)}
                    </Typography>
                  )}
                </Stack>
              ))}
            </Stack>
          ) : (
            <Typography sx={{ color: FP.color.textSecondary }}>
              No hay pagos para esta matrícula
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setAccion(null)} sx={btnOutline(FP.color.textSecondary)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Garantes (consultargarante): fiador registrado en la solicitud */}
      <Dialog
        open={accion === "garantes"}
        onClose={() => setAccion(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 2.5 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Consulta de garante</DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 2 }}>
            <TextField
              fullWidth
              label="Cédula del cliente"
              value={cedulaGar}
              onChange={(e) => setCedulaGar(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && consultarGarante()}
              sx={fpInputSx}
            />
            <Button
              disableElevation
              startIcon={
                consultandoGar ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
              }
              onClick={consultarGarante}
              disabled={consultandoGar}
              sx={btnSolid(FP.color.accentBlue, FP.color.navy)}
            >
              Buscar
            </Button>
          </Stack>
          {garante ? (
            <Grid container spacing={2}>
              <Dato label="Garante" value={garante.fiador} full />
              <Dato label="Dirección" value={garante.fiadordir} full />
              <Dato label="Teléfono" value={garante.fiadortel} full />
            </Grid>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setAccion(null)} sx={btnOutline(FP.color.textSecondary)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reimprimir recibos (reimprimirpagos): recibos del préstamo cargado */}
      <Dialog
        open={accion === "recibos"}
        onClose={() => setAccion(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 2.5 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Reimprimir recibo — préstamo #{noprest}</DialogTitle>
        <DialogContent>
          {recibos === null ? (
            <Stack alignItems="center" sx={{ py: 3 }}>
              <CircularProgress size={26} />
            </Stack>
          ) : recibos.length ? (
            <Stack divider={<Divider flexItem />} spacing={1}>
              {recibos.map((r) => (
                <Stack
                  key={r.norecibo}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={1}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.9rem" }}>
                      Recibo #{r.norecibo} — {r.fecha}
                    </Typography>
                    <Typography sx={{ fontSize: "0.8rem", color: FP.color.textSecondary }}>
                      RD$ {fmtMoney(r.total)}
                      {toNum(r.vpagmora) > 0 ? ` (mora RD$ ${fmtMoney(r.vpagmora)})` : ""}
                    </Typography>
                  </Box>
                  {puedeReimprimir && (
                    <Button
                      size="small"
                      startIcon={
                        reimprimiendo === r.norecibo ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : (
                          <PrintOutlinedIcon />
                        )
                      }
                      disabled={reimprimiendo !== null}
                      onClick={() => reimprimirRecibo(r.norecibo)}
                      sx={btnOutline(FP.color.accentBlue)}
                    >
                      Imprimir
                    </Button>
                  )}
                </Stack>
              ))}
            </Stack>
          ) : (
            <Typography sx={{ color: FP.color.textSecondary }}>
              El préstamo no tiene recibos registrados
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setAccion(null)} sx={btnOutline(FP.color.textSecondary)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modificar foto (modificarfoto): reemplaza expedienteunico.foto */}
      <Dialog
        open={accion === "foto"}
        onClose={() => setAccion(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 2.5 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Foto del cliente</DialogTitle>
        <DialogContent>
          <Stack alignItems="center" spacing={2} sx={{ py: 1 }}>
            {foto === null && !fotoNueva ? (
              <CircularProgress size={26} />
            ) : fotoNueva || foto ? (
              <Box
                component="img"
                alt="Foto del cliente"
                src={`data:image/jpeg;base64,${fotoNueva || foto}`}
                sx={{
                  maxWidth: "100%",
                  maxHeight: 260,
                  borderRadius: 2,
                  border: `1px solid ${FP.color.border}`,
                }}
              />
            ) : (
              <Typography sx={{ color: FP.color.textSecondary }}>
                El cliente no tiene foto registrada
              </Typography>
            )}
            <Button component="label" sx={btnOutline(FP.color.navy)}>
              Seleccionar imagen
              <input hidden type="file" accept="image/*" onChange={seleccionarFoto} />
            </Button>
            {fotoNueva ? (
              <Typography sx={{ fontSize: "0.8rem", color: FP.color.textSecondary }}>
                Vista previa de la imagen nueva — guarde para aplicarla
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setAccion(null)} sx={btnOutline(FP.color.textSecondary)}>
            Cancelar
          </Button>
          <Button
            disableElevation
            startIcon={
              guardandoFoto ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />
            }
            onClick={guardarFoto}
            disabled={guardandoFoto || !fotoNueva}
            sx={btnSolid(FP.color.accentBlue, FP.color.navy)}
          >
            Guardar foto
          </Button>
        </DialogActions>
      </Dialog>

      {/* Evaluación (evaluacion.py): calculadora de préstamos */}
      <Dialog
        open={accion === "evaluacion"}
        onClose={() => setAccion(null)}
        fullWidth
        maxWidth="lg"
        PaperProps={{ sx: { borderRadius: 2.5 } }}
      >
        <DialogContent sx={{ p: 0 }}>
          {accion === "evaluacion" && <CalculadoraPrestamos />}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAccion(null)} sx={btnOutline(FP.color.textSecondary)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// Celda de dato general (etiqueta arriba, valor abajo). `money` formatea RD$;
// `full` ocupa el ancho completo (comentarios/notas); `strong` resalta.
function Dato({ label, value, money, full, strong }) {
  const display = money
    ? `RD$ ${fmtMoney(value)}`
    : value === "" || value === null || value === undefined
    ? "—"
    : String(value);
  return (
    <Grid item xs={6} sm={4} md={full ? 12 : 2}>
      <Typography
        sx={{
          fontSize: "0.68rem",
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          color: FP.color.textMuted,
          fontWeight: 700,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: strong ? "0.98rem" : "0.9rem",
          fontWeight: strong ? 800 : 700,
          color: strong ? FP.color.navy : FP.color.textPrimary,
          fontVariantNumeric: "tabular-nums",
          whiteSpace: full ? "normal" : "nowrap",
        }}
      >
        {display}
      </Typography>
    </Grid>
  );
}

function Row({ label, value, bold, warn, info }) {
  const color = warn ? FP.color.warning : info ? FP.color.info : null;
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        sx={{
          fontSize: bold ? "0.95rem" : "0.86rem",
          fontWeight: bold ? 800 : 600,
          color: color || FP.color.textSecondary,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: bold ? "1rem" : "0.9rem",
          fontWeight: bold ? 800 : 700,
          color: color || FP.color.textPrimary,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {info ? "− " : ""}RD$ {fmtMoney(value)}
      </Typography>
    </Stack>
  );
}
